
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProperties } from '@/hooks/useProperties';
import { useBatchedAvailability } from '@/hooks/useBatchedAvailability';
import { isDateRangeAvailable } from '@/utils/dateAvailability';
import { parseDateFromUrl } from '@/utils/dateParser';
import { useDebounce } from '@/hooks/useDebounce';
import { Property } from '@/types/property';

export const useSearchResultsFilter = () => {
  const [searchParams] = useSearchParams();
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  
  // Get raw search params
  const guests = searchParams.get('guests');
  const checkin = searchParams.get('checkin');
  const checkout = searchParams.get('checkout');

  // Debounce search parameters to reduce API calls (500ms delay)
  const debouncedGuests = useDebounce(guests, 500);
  const debouncedCheckin = useDebounce(checkin, 500);
  const debouncedCheckout = useDebounce(checkout, 500);
  
  // Get properties from Supabase
  const { data: properties = [], isLoading: propertiesLoading, error: propertiesError } = useProperties();

  // Get all property IDs for batched availability
  const propertyIds = properties.map(p => p.id);
  
  // Use batched availability instead of individual hooks
  const batchedAvailability = useBatchedAvailability(propertyIds);

  useEffect(() => {
    let filtered = properties.filter(property => property.available);

    // Filter by guest capacity
    if (debouncedGuests) {
      const guestCount = parseInt(debouncedGuests);
      filtered = filtered.filter(property => property.guests >= guestCount);
    }

    // Filter by date availability using batched Supabase data
    if (debouncedCheckin && debouncedCheckout) {
      const checkInDate = parseDateFromUrl(debouncedCheckin);
      const checkOutDate = parseDateFromUrl(debouncedCheckout);
      
      if (checkInDate && checkOutDate) {
        // Check if any availability data is still loading
        const isAnyAvailabilityLoading = Object.values(batchedAvailability).some(av => av.isLoading);
        
        if (!isAnyAvailabilityLoading) {
          filtered = filtered.filter(property => {
            const propertyAvailability = batchedAvailability[property.id];
            
            if (!propertyAvailability) {
              return true; // Assume available if no data
            }
            
            const unavailableDates = propertyAvailability.unavailableDates || [];
            return isDateRangeAvailable(checkInDate, checkOutDate, unavailableDates);
          });
        }
      }
    }

    setFilteredProperties(filtered);
  }, [debouncedGuests, debouncedCheckin, debouncedCheckout, properties, batchedAvailability]);

  const isAnyLoading = propertiesLoading || Object.values(batchedAvailability).some(av => av.isLoading);
  const hasErrors = !!propertiesError || Object.values(batchedAvailability).some(av => !!av.error);

  return {
    filteredProperties,
    isAnyLoading,
    hasErrors,
    refetchAll: () => {
      // The query will automatically refetch when needed
    }
  };
};
